<template>
    <FrequencyWarningSnippet v-if="showFrequencyWarning && !renderStore.builderMode"
        :frequency="renderStore.gift.frequency" class="rd-mb-3" />

    <template v-if="showProjects">
        <ProjectContainerComponent :component="component" :useSmartGiftArray="useSmartGiftArray" />
    </template>

    <template v-if="showGiftArray">
        <GiftArrayComponent :component="component" :useSmartGiftArray="useSmartGiftArray" />
    </template>

    <ValidatedInput :id="component.id" :displayName="'Gift Amount'" :value="renderStore.amount" :isRequired="true"
        :isStepError="true" name="Amount">
        <template #input>
            <span
                class="rd-block rd-font-semibold rd-tracking-wide rd-leading-none rd-mt-3 rd-text-center rd-uppercase gift-total"
                :class="{ 'rd-small-caps': renderStore.builderMode }">Total: {{ formattedTotal }}</span>
        </template>
    </ValidatedInput>
</template>

<script lang="ts">
import { hasComponentByType } from "@/builder/BuilderUtilities";
import ValidatedInput from "@/builder/components/input/ValidatedInput.vue";
import { IDonationBlockComponent } from "@/builder/interfaces/index";
import { useRenderStore } from "@/builder/stores/renderStore";
import { formatCurrency } from "@/utilities/StringUtilities";
import { defineComponent, PropType } from "vue";
import { ComponentType, GiftArrayType, ProjectDisplayType } from "../../enums";
import FrequencyWarningSnippet from "../frequency/FrequencyWarningSnippet.vue";
import GiftArrayComponent from "./GiftArrayComponent.vue";
import ProjectContainerComponent from "./ProjectContainerComponent.vue";

export default defineComponent({
    name: "DonationBlockComponent",
    props: {
        component: {
            type: Object as PropType<IDonationBlockComponent>,
            required: true
        }
    },
    components: {
        GiftArrayComponent,
        ProjectContainerComponent,
        ValidatedInput,
        FrequencyWarningSnippet
    },
    setup() {
        const renderStore = useRenderStore();
        return { renderStore };
    },
    computed: {
        showProjects() {
            const { projectSettings } = this.component.customData;
            return projectSettings.showProjects && !this.renderStore.page.projectOverrideCode;
        },

        formattedTotal() {
            if (this.renderStore.gift.coverCosts) {
                return '$' + formatCurrency(String(this.renderStore.totalAmountWithCosts));
            }

            return '$' + formatCurrency(String(this.renderStore.totalAmount));
        },

        showGiftArray() {
            const { projectSettings } = this.component.customData;
            return (!projectSettings.showGiftArray &&
                !projectSettings.showCustomAmount &&
                !projectSettings.allowMultipleProjects) ||
                projectSettings.displayType === ProjectDisplayType.Dropdown ||
                this.renderStore.page.projectOverrideCode;
        },

        useSmartGiftArray() {
            return this.component.customData.useSmartGiftArray;
        },

        showFrequencyWarning() {
            return (!hasComponentByType(this.renderStore.allComponents, ComponentType.Frequency) &&
                this.renderStore.isRecurring);
        }
    },
    methods: {
        setProjectOverride() {
            if (this.renderStore.page.projectPreselect && !this.renderStore.page.projectOverrideCode && !this.showProjects) {
                //when project preselect is set and showProjects is false, we treat preset project as override
                this.renderStore.page.projectOverrideCode = this.renderStore.page.projectPreselect.code;
            }
        }
    },
    watch: {
        'component.customData.fixedAmount': {
            handler(fixedAmount: number) {
                //if page doesnt have projects, set the amount to the fixed amount
                if (!this.showProjects && this.component.customData.giftArrayType === GiftArrayType.FixedAmount) {
                    this.renderStore.setAmount(fixedAmount.toString());
                }
            },
            immediate: true
        },
        'renderStore.page.projectPreselect': {
            handler() {
                this.setProjectOverride();
            },
            immediate: true
        }
    }
})
</script>
